// Essen – Kalorien und Makros, pro Nutzer.
//
// Neu: Protein/Fett/Kohlenhydrate (die App empfahl bisher Protein-Ziele,
// konnte sie aber nicht tracken), Mahlzeitentypen, Tagesnavigation,
// Verlaufsdiagramme und „zuletzt gegessen" für Ein-Tipp-Wiederholungen.
import {
  MEAL_TYPES,
  defaultMealType,
  formatGrams,
  formatKcal,
  localDate,
  localDateOffset,
} from '@speis/shared';
import type { FoodDayResponse, FoodEntryView, FoodInfo, UserView } from '@speis/shared';
import { api } from '../core/api.js';
import { guard, guarded } from '../core/guard.js';
import { queue } from '../core/outbox.js';
import { state } from '../core/store.js';
import { clear, el, haptic, replace } from '../ui/dom.js';
import { lineChart, ring } from '../ui/chart.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';
import { toast } from '../ui/toast.js';
import { scanBarcode } from '../flows/scanner.js';
import { openSettings } from './settings.js';
import { skeletonList } from './shared.js';

let day = localDate();
let host: HTMLElement | null = null;

export function renderFood(container: HTMLElement): void {
  host = container;
  clear(container);
  container.append(skeletonList(3));
  void load();
}

export function teardownFood(): void {
  host = null;
}

function me(): UserView | null {
  return state.me;
}

async function load(): Promise<void> {
  if (!host) return;
  const user = me();
  if (!user) {
    replace(host, el('p', { class: 'muted' }, 'Profil nicht geladen.'));
    return;
  }
  if (user.calorieMode === 'gentle') {
    await renderGentle();
    return;
  }
  await renderCounting();
}

// ── Modus „zählen" ────────────────────────────────────────────────────

async function renderCounting(): Promise<void> {
  if (!host) return;

  const data = await guard(() => api<FoodDayResponse>(`/api/food/log?date=${day}`), {
    failure: 'Tagebuch konnte nicht geladen werden',
  });
  if (!data || !host) return;

  clear(host);
  host.append(dayNav());

  // Ringe: Kalorien groß, Makros daneben.
  const rings = el('div', { class: 'ring-row' }, [
    ring({
      value: data.totalKcal,
      max: data.targets.kcal ?? Math.max(data.totalKcal, 1),
      label: data.targets.kcal ? `von ${data.targets.kcal}` : 'kcal',
      display: String(data.totalKcal),
      size: 96,
    }),
    data.totals.protein != null
      ? ring({
          value: data.totals.protein,
          max: data.targets.proteinMin ?? Math.max(data.totals.protein, 1),
          label: 'Protein',
          display: formatGrams(data.totals.protein),
          color: 'var(--info)',
        })
      : null,
    data.totals.carbs != null
      ? ring({
          value: data.totals.carbs,
          max: Math.max(data.totals.carbs, 1),
          label: 'Kohlenhydr.',
          display: formatGrams(data.totals.carbs),
          color: 'var(--accent)',
        })
      : null,
    data.totals.fat != null
      ? ring({
          value: data.totals.fat,
          max: Math.max(data.totals.fat, 1),
          label: 'Fett',
          display: formatGrams(data.totals.fat),
          color: 'var(--warn)',
        })
      : null,
  ]);
  host.append(el('div', { class: 'card' }, rings));

  if (data.targets.kcal && data.totalKcal > data.targets.kcal) {
    host.append(
      el(
        'p',
        { class: 'notice notice-warn' },
        `${data.totalKcal - data.targets.kcal} kcal über dem Tagesziel – morgen ist auch noch ein Tag.`,
      ),
    );
  }
  if (data.targets.proteinMin && (data.totals.protein ?? 0) < data.targets.proteinMin) {
    host.append(
      el(
        'p',
        { class: 'tiny' },
        `Protein-Richtwert: ${data.targets.proteinMin}–${data.targets.proteinMax} g/Tag.`,
      ),
    );
  }

  host.append(actionRow(false));
  await appendFavorites(host, false);
  await appendRecent(host);

  host.append(entriesByMeal(data.entries, false));
  await appendHistory(host);
}

function dayNav(): HTMLElement {
  const today = localDate();
  const label =
    day === today
      ? 'Heute'
      : day === localDateOffset(-1)
        ? 'Gestern'
        : new Date(`${day}T12:00:00`).toLocaleDateString('de-DE', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
          });

  return el('div', { class: 'week-nav' }, [
    el(
      'button',
      {
        class: 'icon-btn',
        type: 'button',
        'aria-label': 'Vorheriger Tag',
        onclick: () => {
          day = localDateOffset(-1, new Date(`${day}T12:00:00`));
          void load();
        },
      },
      icon('chevronLeft'),
    ),
    el('div', { class: 'week-title' }, [el('strong', {}, label)]),
    el(
      'button',
      {
        class: 'icon-btn',
        type: 'button',
        'aria-label': 'Nächster Tag',
        disabled: day >= today,
        onclick: () => {
          day = localDateOffset(1, new Date(`${day}T12:00:00`));
          void load();
        },
      },
      icon('chevronRight'),
    ),
    day !== today
      ? el(
          'button',
          {
            class: 'btn btn-sm btn-ghost',
            type: 'button',
            onclick: () => {
              day = today;
              void load();
            },
          },
          'Heute',
        )
      : null,
  ]);
}

function actionRow(quiet: boolean): HTMLElement {
  return el('div', { class: 'btn-row food-actions' }, [
    el(
      'button',
      { class: 'btn btn-secondary', type: 'button', onclick: () => void barcodeFlow(quiet) },
      [icon('barcode', { size: 18 }), 'Barcode'],
    ),
    el('button', { class: 'btn btn-secondary', type: 'button', onclick: () => openSearch(quiet) }, [
      icon('search', { size: 18 }),
      'Suchen',
    ]),
    quiet
      ? null
      : el('button', { class: 'btn btn-secondary', type: 'button', onclick: openWeightSheet }, [
          icon('scale', { size: 18 }),
          'Gewicht',
        ]),
  ]);
}

function entriesByMeal(entries: FoodEntryView[], quiet: boolean): HTMLElement {
  const wrap = el('div', { class: 'screen-section' });

  if (!entries.length) {
    wrap.append(
      el('div', { class: 'empty' }, [
        el('div', { class: 'empty-icon', 'aria-hidden': 'true' }, '🍽️'),
        el(
          'p',
          { class: 'empty-title' },
          quiet ? 'Noch nichts notiert' : 'Heute noch nichts geloggt',
        ),
      ]),
    );
    return wrap;
  }

  for (const meal of MEAL_TYPES) {
    const forMeal = entries.filter((e) => (e.mealType ?? 'snack') === meal.key);
    if (!forMeal.length) continue;

    const sum = forMeal.reduce((s, e) => s + (e.kcal ?? 0), 0);
    wrap.append(
      el('h2', { class: 'cat-head' }, [
        el('span', { class: 'cat-icon', 'aria-hidden': 'true' }, meal.icon),
        el('span', {}, meal.label),
        quiet ? null : el('span', { class: 'cat-count tiny tabular' }, formatKcal(sum)),
      ]),
    );

    const list = el('ul', { class: 'list' });
    for (const entry of forMeal) list.append(entryRow(entry, quiet));
    wrap.append(list);
  }

  return wrap;
}

function entryRow(entry: FoodEntryView, quiet: boolean): HTMLElement {
  const meta = el('div', { class: 'item-meta' });
  if (!quiet) {
    meta.append(el('span', { class: 'tabular' }, formatKcal(entry.kcal)));
    if (entry.portion) meta.append(el('span', {}, entry.portion));
    if (entry.protein != null)
      meta.append(el('span', { class: 'tiny' }, `P ${formatGrams(entry.protein)}`));
  } else if (entry.portion) {
    meta.append(el('span', {}, entry.portion));
  }

  return el('li', { class: 'list-item' }, [
    el('div', { class: 'item-body' }, [
      el('div', { class: 'item-name' }, entry.label),
      meta.childElementCount ? meta : null,
    ]),
    el(
      'button',
      {
        class: 'icon-btn',
        type: 'button',
        'aria-label': `${entry.label} entfernen`,
        onclick: guarded(async () => {
          await queue('food.delete', { id: entry.id });
          await load();
        }),
      },
      icon('trash', { size: 18 }),
    ),
  ]);
}

async function appendFavorites(container: HTMLElement, quiet: boolean): Promise<void> {
  const result = await guard(() => api<{ favorites: FavoriteView[] }>('/api/food/favorites'), {
    failure: 'Favoriten',
  });
  const favorites = result?.favorites ?? [];
  if (!favorites.length) return;

  const row = el('div', { class: 'chip-row' });
  for (const f of favorites) {
    row.append(
      el(
        'button',
        {
          class: 'chip',
          type: 'button',
          onclick: guarded(
            async () => {
              haptic(10);
              await api(`/api/food/favorites/${f.id}/log`, {
                method: 'POST',
                body: { mealType: defaultMealType() },
              });
              await load();
            },
            { success: quiet ? 'Notiert' : 'Geloggt' },
          ),
        },
        [icon('star', { size: 14 }), quiet ? f.name : `${f.name}${f.kcal ? ` · ${f.kcal}` : ''}`],
      ),
    );
  }

  container.append(el('h2', { class: 'eyebrow section-gap' }, 'Favoriten – ein Tipp genügt'), row);
}

interface FavoriteView {
  id: number;
  name: string;
  kcal: number | null;
  portion: string | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
}

interface RecentEntry {
  label: string;
  kcal: number;
  portion: string | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
  uses: number;
}

async function appendRecent(container: HTMLElement): Promise<void> {
  const result = await guard(() => api<{ recent: RecentEntry[] }>('/api/food/recent'), {
    failure: 'Verlauf',
  });
  const recent = result?.recent ?? [];
  if (!recent.length) return;

  const row = el('div', { class: 'chip-row' });
  for (const r of recent.slice(0, 8)) {
    row.append(
      el(
        'button',
        {
          class: 'chip',
          type: 'button',
          onclick: guarded(
            async () => {
              await api('/api/food/log', {
                method: 'POST',
                body: {
                  label: r.label,
                  kcal: r.kcal,
                  portion: r.portion,
                  protein: r.protein,
                  carbs: r.carbs,
                  fat: r.fat,
                  date: day,
                  mealType: defaultMealType(),
                  source: 'favorite',
                },
              });
              await load();
            },
            { success: 'Geloggt' },
          ),
        },
        `${r.label} · ${r.kcal}`,
      ),
    );
  }

  container.append(el('h2', { class: 'eyebrow section-gap' }, 'Zuletzt gegessen'), row);
}

async function appendHistory(container: HTMLElement): Promise<void> {
  const result = await guard(
    () =>
      api<{
        kcal: {
          points: { date: string; value: number | null }[];
          average: number | null;
          daysLogged: number;
        };
        macros: {
          protein: number | null;
          carbs: number | null;
          fat: number | null;
          daysLogged: number;
        };
        target: number | null;
      }>('/api/food/history?days=30'),
    { failure: 'Verlauf' },
  );
  if (!result || result.kcal.daysLogged < 2) return;

  container.append(
    el('div', { class: 'card screen-section' }, [
      el('div', { class: 'section-title' }, [
        el('span', {}, 'Letzte 30 Tage'),
        result.kcal.average
          ? el('span', { class: 'tiny tabular' }, `Ø ${formatKcal(result.kcal.average)}`)
          : null,
      ]),
      lineChart({
        points: result.kcal.points,
        target: result.target,
        label: 'Kalorien pro Tag',
        formatValue: (v) => String(Math.round(v)),
      }),
      result.macros.protein != null
        ? el(
            'p',
            { class: 'tiny' },
            `Ø Makros: ${formatGrams(result.macros.protein)} Protein · ${formatGrams(result.macros.carbs)} KH · ${formatGrams(result.macros.fat)} Fett`,
          )
        : null,
    ]),
  );
}

// ── Modus „sanft" ─────────────────────────────────────────────────────

async function renderGentle(): Promise<void> {
  if (!host) return;
  clear(host);

  const info = await guard(
    () => api<{ hasEatenToday: boolean; level: string; suggestion: string }>('/api/food/evening'),
    { failure: 'Hinweis' },
  );
  if (!host) return;

  const hour = new Date().getHours();
  if (info && info.level !== 'ok' && hour >= 18 && hour < 21) {
    host.append(
      el(
        'div',
        { class: info.level === 'strong' ? 'notice notice-warn' : 'notice' },
        info.suggestion,
      ),
    );
  } else if (info?.hasEatenToday) {
    host.append(el('p', { class: 'notice' }, 'Alles gut für heute. 🙂'));
  } else {
    host.append(el('p', { class: 'notice' }, 'Kein Stress – hier zählt niemand mit. 🙂'));
  }

  host.append(actionRow(true));

  host.append(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block section-gap',
        type: 'button',
        onclick: guarded(
          async () => {
            await api('/api/food/log', {
              method: 'POST',
              body: { label: 'Habe gegessen', date: day, mealType: defaultMealType() },
            });
            await load();
          },
          { success: 'Schön! 🙂' },
        ),
      },
      [icon('check', { size: 18 }), 'Ich habe etwas gegessen'],
    ),
  );

  await appendFavorites(host, true);

  const data = await guard(() => api<FoodDayResponse>(`/api/food/log?date=${day}`), {
    failure: 'Tagebuch',
  });
  if (data?.entries.length && host) {
    host.append(el('h2', { class: 'eyebrow section-gap' }, 'Heute notiert'));
    // Bewusst ohne Zahlen: im Sanft-Modus ist das ein Tagebuch, keine Bilanz.
    host.append(entriesByMeal(data.entries, true));
  }
}

// ── Nachschlagen & Eintragen ──────────────────────────────────────────

async function barcodeFlow(quiet: boolean): Promise<void> {
  const code = await scanBarcode();
  if (!code) return;

  const result = await guard(
    () => api<{ food: FoodInfo }>('/api/food/lookup', { method: 'POST', body: { barcode: code } }),
    { failure: 'Nährwerte' },
  );
  if (result) openPortionSheet(result.food, quiet, 'barcode', code);
  else openManualSheet('', quiet);
}

function openSearch(quiet: boolean): void {
  const input = el('input', {
    class: 'input',
    type: 'search',
    placeholder: 'z.B. Haferflocken oder Pizza Salami',
    enterkeyhint: 'search',
  });
  const results = el('div', { class: 'results' });
  const body = el('div', {}, [
    el('div', { class: 'field' }, [el('label', {}, 'Was hast du gegessen?'), input]),
    results,
  ]);
  const sheet = openSheet(body, { title: 'Essen suchen' });

  const search = async (): Promise<void> => {
    const q = input.value.trim();
    if (q.length < 2) return;
    replace(results, el('span', { class: 'spinner' }));

    const data = await guard(
      () =>
        api<{
          catalog: { id: number; name: string; brand: string | null; barcode: string | null }[];
          off: FoodInfo[];
        }>(`/api/food/search?q=${encodeURIComponent(q)}`),
      { failure: 'Suche' },
    );
    clear(results);

    if (data?.catalog.length) {
      results.append(el('p', { class: 'eyebrow' }, 'Aus eurem Vorrat'));
      for (const p of data.catalog) {
        results.append(
          el(
            'button',
            {
              class: 'result',
              type: 'button',
              onclick: guarded(async () => {
                sheet.close();
                if (p.barcode) {
                  const r = await api<{ food: FoodInfo }>('/api/food/lookup', {
                    method: 'POST',
                    body: { barcode: p.barcode },
                  }).catch(() => null);
                  if (r) {
                    openPortionSheet(
                      { ...r.food, name: r.food.name ?? p.name },
                      quiet,
                      'barcode',
                      p.barcode,
                    );
                    return;
                  }
                }
                await estimate(p.brand ? `${p.name} ${p.brand}` : p.name, quiet);
              }),
            },
            p.brand ? `${p.name} · ${p.brand}` : p.name,
          ),
        );
      }
    }

    if (data?.off.length) {
      results.append(el('p', { class: 'eyebrow' }, 'Lebensmittel-Bibliothek'));
      for (const f of data.off) {
        results.append(
          el(
            'button',
            {
              class: 'result',
              type: 'button',
              onclick: () => {
                sheet.close();
                openPortionSheet(f, quiet, 'text', f.barcode ?? null);
              },
            },
            [
              el('div', { class: 'item-body' }, [
                el(
                  'div',
                  { class: 'item-name' },
                  f.brand ? `${f.name} · ${f.brand}` : (f.name ?? ''),
                ),
                quiet
                  ? null
                  : el(
                      'div',
                      { class: 'item-meta tabular' },
                      `${f.kcalPer100} kcal/100 ${f.baseUnit}`,
                    ),
              ]),
            ],
          ),
        );
      }
    }

    if (!data?.catalog.length && !data?.off.length) {
      results.append(el('p', { class: 'tiny' }, 'Keine Treffer in Vorrat und Bibliothek.'));
    }

    results.append(
      el(
        'button',
        {
          class: 'result result-new',
          type: 'button',
          onclick: async () => {
            sheet.close();
            await estimate(q, quiet);
          },
        },
        [icon('sparkles', { size: 18 }), `KI schätzen lassen: „${q}"`],
      ),
      el(
        'button',
        {
          class: 'result',
          type: 'button',
          onclick: () => {
            sheet.close();
            openManualSheet(q, quiet);
          },
        },
        [icon('edit', { size: 18 }), 'Manuell eintragen'],
      ),
    );
  };

  input.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') void search();
  });
  sheet.addActions(
    el(
      'button',
      { class: 'btn btn-primary btn-block', type: 'button', onclick: () => void search() },
      'Suchen',
    ),
  );
}

async function estimate(text: string, quiet: boolean): Promise<void> {
  const result = await guard(
    () => api<{ food: FoodInfo }>('/api/food/lookup', { method: 'POST', body: { text } }),
    { failure: 'Schätzung fehlgeschlagen' },
  );
  if (result) openPortionSheet(result.food, quiet, 'text', null);
  else openManualSheet(text, quiet);
}

function openPortionSheet(
  food: FoodInfo,
  quiet: boolean,
  source: string,
  barcode: string | null,
): void {
  const name = food.name ?? 'Unbenannt';
  const unit = food.baseUnit || 'g';

  const portion = el('input', {
    class: 'input',
    type: 'number',
    inputmode: 'numeric',
    min: 1,
    value: food.portionG ?? 100,
  });
  const meal = mealSelect();
  const preview = el('div', { class: 'kcal-preview tabular' });

  const scaled = () => {
    const grams = Number(portion.value) || 0;
    const factor = grams / 100;
    return {
      grams,
      kcal: food.kcalPer100 != null ? Math.round(food.kcalPer100 * factor) : null,
      protein: food.protein != null ? Math.round(food.protein * factor * 10) / 10 : null,
      carbs: food.carbs != null ? Math.round(food.carbs * factor * 10) / 10 : null,
      fat: food.fat != null ? Math.round(food.fat * factor * 10) / 10 : null,
    };
  };

  const update = () => {
    if (quiet) {
      preview.textContent = '';
      return;
    }
    const s = scaled();
    preview.textContent = s.kcal != null ? `≈ ${s.kcal} kcal` : '? kcal';
    if (s.protein != null) {
      preview.textContent += `  ·  P ${formatGrams(s.protein)} · KH ${formatGrams(s.carbs)} · F ${formatGrams(s.fat)}`;
    }
  };
  portion.addEventListener('input', update);

  const body = el('div', {}, [
    el('p', { class: 'item-name' }, food.brand ? `${name} · ${food.brand}` : name),
    quiet || food.kcalPer100 == null
      ? null
      : el(
          'p',
          { class: 'tiny tabular' },
          `${food.kcalPer100} kcal / 100 ${unit} (${food.source})`,
        ),
    el('div', { class: 'field' }, [el('label', {}, 'Mahlzeit'), meal]),
    el('div', { class: 'field' }, [el('label', {}, `Portion (${unit})`), portion, preview]),
  ]);

  const sheet = openSheet(body, { title: 'Eintragen' });
  update();

  const log = async (alsoFavorite: boolean): Promise<void> => {
    const s = scaled();
    await api('/api/food/log', {
      method: 'POST',
      body: {
        label: name,
        kcal: s.kcal,
        protein: s.protein,
        carbs: s.carbs,
        fat: s.fat,
        portion: `${s.grams} ${unit}`,
        mealType: meal.value,
        date: day,
        barcode,
        source,
      },
    });
    if (alsoFavorite) {
      await api('/api/food/favorites', {
        method: 'POST',
        body: {
          name,
          kcal: s.kcal,
          protein: s.protein,
          carbs: s.carbs,
          fat: s.fat,
          portion: `${s.grams} ${unit}`,
        },
      });
    }
    sheet.close();
    await load();
  };

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(() => log(false), { success: quiet ? 'Notiert' : 'Geloggt' }),
      },
      'Eintragen',
    ),
    el(
      'button',
      {
        class: 'btn btn-secondary btn-block',
        type: 'button',
        onclick: guarded(() => log(true), { success: 'Geloggt & gemerkt' }),
      },
      [icon('star', { size: 18 }), 'Eintragen & als Favorit'],
    ),
  );
}

function openManualSheet(prefill: string, quiet: boolean): void {
  const name = el('input', {
    class: 'input',
    type: 'text',
    value: prefill,
    placeholder: 'Bezeichnung',
  });
  const kcal = el('input', {
    class: 'input',
    type: 'number',
    inputmode: 'numeric',
    min: 0,
    placeholder: 'kcal gesamt',
  });
  const protein = el('input', {
    class: 'input',
    type: 'number',
    inputmode: 'numeric',
    min: 0,
    placeholder: 'Protein g',
  });
  const meal = mealSelect();

  const body = el('div', {}, [
    el('div', { class: 'field' }, [el('label', {}, 'Bezeichnung'), name]),
    el('div', { class: 'field' }, [el('label', {}, 'Mahlzeit'), meal]),
    quiet
      ? null
      : el('div', { class: 'grid-2' }, [
          el('div', { class: 'field' }, [el('label', {}, 'Kalorien'), kcal]),
          el('div', { class: 'field' }, [el('label', {}, 'Protein'), protein]),
        ]),
  ]);

  const sheet = openSheet(body, { title: 'Manuell eintragen' });

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            if (!name.value.trim()) {
              toast('Bitte eine Bezeichnung eintragen');
              return;
            }
            await queue('food.add', {
              label: name.value.trim(),
              kcal: kcal.value ? Number(kcal.value) : null,
              protein: protein.value ? Number(protein.value) : null,
              date: day,
              mealType: meal.value,
              source: 'manual',
            });
            sheet.close();
            await load();
          },
          { success: quiet ? 'Notiert' : 'Geloggt' },
        ),
      },
      'Eintragen',
    ),
  );
}

function mealSelect(): HTMLSelectElement {
  const select = el('select', { class: 'select', 'aria-label': 'Mahlzeit' });
  const current = defaultMealType();
  for (const m of MEAL_TYPES) {
    const option = el('option', { value: m.key }, `${m.icon} ${m.label}`);
    if (m.key === current) option.selected = true;
    select.append(option);
  }
  return select;
}

// ── Gewicht ───────────────────────────────────────────────────────────

export function openWeightSheet(): void {
  const kg = el('input', {
    class: 'input',
    type: 'text',
    inputmode: 'decimal',
    placeholder: 'z.B. 81,4',
    'aria-label': 'Gewicht in Kilogramm',
  });
  const chart = el('div', { class: 'screen-section' });

  const body = el('div', {}, [
    el('div', { class: 'field' }, [el('label', {}, 'Gewicht heute (kg)'), kg]),
    el(
      'p',
      { class: 'field-hint' },
      'Regelmäßiges Wiegen macht dein Tagesziel mit der Zeit präziser: Aus Gewichtstrend und geloggten Kalorien berechnet die App deinen echten Verbrauch – genauer als jede Formel.',
    ),
    chart,
  ]);

  const sheet = openSheet(body, { title: 'Gewicht' });

  void guard(
    async () => {
      const series = await api<{
        points: { date: string; value: number | null }[];
        trend: { date: string; value: number | null }[];
        changeKg: number | null;
      }>('/api/me/weight?days=90');
      if (series.points.length < 2) return;
      replace(chart, [
        el('div', { class: 'section-title' }, [
          el('span', {}, 'Letzte 90 Tage'),
          series.changeKg != null
            ? el(
                'span',
                { class: 'tiny tabular' },
                `${series.changeKg > 0 ? '+' : ''}${series.changeKg} kg`,
              )
            : null,
        ]),
        lineChart({
          points: series.points,
          trend: series.trend,
          label: 'Gewicht',
          formatValue: (v) => `${v.toFixed(1)}`,
        }),
      ]);
    },
    { failure: 'Gewichtsverlauf' },
  );

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            const value = Number(kg.value.replace(',', '.'));
            if (!value) {
              toast('Bitte ein Gewicht eintragen');
              return;
            }
            await api('/api/me/weight', { method: 'POST', body: { weightKg: value } });
            sheet.close();
          },
          { success: 'Gewicht gespeichert' },
        ),
      },
      'Speichern',
    ),
  );
}

export { openSettings };
